import "./FormRenderer.css"

import { useEffect, useState, useRef } from "react"

import { PDFDocument } from 'pdf-lib'
import { Document, Page, pdfjs } from 'react-pdf'

import { DownloadOutlined, SaveOutlined } from '@ant-design/icons'
import { Button, Tag } from "antd"
import { MinusOutlined, PlusOutlined } from "@ant-design/icons"

import "react-pdf/dist/esm/Page/TextLayer.css"
import "react-pdf/dist/esm/Page/AnnotationLayer.css"

import { PDFTextField, PDFCheckBox} from "pdf-lib"

const FormRenderer = ({ pdfData, handleSaveForm, formFieldValues, requiredFields, handleDownloadForm, defaultScale, fieldOptions }) => {

    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`

    const [file, setFile] = useState(null)
    const [pages, setPages] = useState([])

    const [formFields, setFormFields] = useState([])
    const [pageSizes, setPageSizes] = useState([])   
    const [formValues, setFormValues] = useState({})
    const [pageHeight, setPageHeight] = useState(0)
    const [pageWidth, setPageWidth] = useState(0)
    const [numPages, setNumPages] = useState(null)
    const [scale, setScale] = useState(1.2)
    const [validations, setValidations] = useState([])

    const inputRefs = useRef({})

    useEffect(() => {
        async function loadPdf() {
            if (pdfData) {
                const pdfDoc = await PDFDocument.load(pdfData)

                if (pdfDoc) {
                    const pdfBytes = await pdfDoc.save()
                    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
                    const url = URL.createObjectURL(pdfBlob);

                    setFile(url)
                    setPages(Array.from({ length: pdfDoc.getPageCount() }, (_, i) => i + 1))
                }
            }
        }
        loadPdf()
    }, [pdfData])

    useEffect(() => {
      if(defaultScale) {
        setScale(defaultScale)
      }
    }, [defaultScale])

    const ptToPx = (points) => {
      const pixels = points * (96 / 72)
      return pixels
    }

    useEffect(() => {
      if(formFieldValues) {
        setFormValues(formFieldValues)
      }
    }, [formFieldValues])

    useEffect(() => {
      extractFormFields()
    }, [pdfData, scale])

    const extractFormFields = async () => {
      const pdfDoc = await PDFDocument.load(pdfData)
      if (!pdfDoc) return

      const pages = pdfDoc.getPages()
      const form = pdfDoc.getForm()

      const pageSizes = []
      const fieldData = []

      pages.forEach((page, pageIndex) => {
        const { width, height } = page.getSize()

        pageSizes[pageIndex] = {
          width: ptToPx(width * scale),
          height: ptToPx(height * scale),
        }
      })

      const fields = form.getFields()

      fields.forEach((field) => {
        if (!formFieldValues.some(f => f.formFieldName === field.getName())) {
          return
        }

        let name = field.getName()
        const fieldType = getFieldType(field)
        const widgets = field.acroField.getWidgets()

        widgets.forEach((widget, index) => {
          const rect = widget.getRectangle()
          const pageIndex = pages.findIndex(p => p.ref === widget.P())

          if (!rect) return

          fieldData.push({
            name: fieldType === 'PDFCheckBox' ? `${name}${index + 1}` : name,
            fieldType,
            pageIndex,
            rect: {
              x: ptToPx(rect.x * scale),
              y: ptToPx(rect.y * scale),
              width: ptToPx(rect.width * scale),
              height: ptToPx(rect.height * scale),
            }
          })
        })
      })

      setPageSizes(pageSizes)
      setFormFields(fieldData)
    }

    const renderFieldOverlay = (field, pageHeight) => {
      const scaledX = field.rect.x * scale
      const scaledY = field.rect.y * scale
      const scaledWidth = field.rect.width * scale
      const scaledHeight = field.rect.height * scale

      const top = pageHeight - (scaledY + scaledHeight)

      return (
        <div className="form-field"
          key={`${field.name}-${field.pageIndex}`}
          style={{
            position: 'absolute',
            top: `${top}px`,
            left: `${scaledX}px`,
            width: `${scaledWidth}px`,
            height: `${scaledHeight}px`,
            zIndex: 10,
          }}
        >
          {createField(field)}
        </div>
      )
    }


    const onLoadSuccess = ({ numPages }) => {
        setNumPages(numPages)
    }

    const handleFieldChange = (fieldName, value) => {
      updateFormValue(value, fieldName)
    }

    const updateFormValue = (value, name) => {
      let updatedFormValues = formValues.map(f => {
          let updatedFormValue = f
          if(f.formFieldName === name) {
              updatedFormValue = {...f, formFieldValue: value}
          }
          return updatedFormValue
      })

      setFormValues(updatedFormValues)
    }

    const handleFocus = (id) => {
      inputRefs.current[id]?.scrollIntoView({ behavior: 'smooth' })
      inputRefs.current[id]?.focus()
    }

    const doFieldHasOptions = (fieldName) => {
      let field = fieldOptions?.filter(f => f.fieldName === fieldName)[0]

      return field != null
    }

    const getFieldOptions = (fieldName) => {
      return fieldOptions?.filter(f => f.fieldName === fieldName).map(f => f.options)[0]
    }

    const createField = (field) => {
      if (field.fieldType === 'PDFTextField') {
        let value = formValues.filter(f => f.formFieldName === field.name)[0]?.formFieldValue
        return (
          doFieldHasOptions(field.name) ?
            <select style={{width: '100%', height: '100%'}} value={value} onChange={(e) => handleFieldChange(field.name, e.target.value)}>
                {getFieldOptions(field.name)?.map(f => <option value={f.value}>{f.label}</option>)}
            </select>
          :
            <input 
              className={(!value && requiredFields?.includes(field.name) ? "required" : "")}
              ref={(el) => {
                    if (el && !inputRefs.current[field.name]) {
                      inputRefs.current[field.name] = el;
                    }
              }}
              style={{width: '100%', height: '100%'}}
              value={value || ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
            />
        )
      } else if (field.fieldType === 'PDFCheckBox') {
          const checkBoxIndex = field.name?.slice(-1)
          let fieldName = field.name?.slice(0, -1)
          let value = formValues.filter(f => f.formFieldName === fieldName)[0]?.formFieldValue
          return (
            <input style={{width: '100%'}} 
              type="checkbox" id={field.name}
              checked={value === checkBoxIndex}
              onChange={(e) => handleFieldChange(fieldName, checkBoxIndex)}
            />
          )
      } else {
        console.log('Unknown field type:', field.fieldType)
      }
    }

    const downloadPdf = async () => {
      handleDownloadForm()
    }

    const getFieldType = (field) => {
      let fieldType
      if(field instanceof PDFTextField) {
        fieldType = 'PDFTextField'
      } else if(field instanceof PDFCheckBox) {
        fieldType = 'PDFCheckBox'
      }

      return fieldType
    }

    const isFormValid = () => {
      let isValid = true

      requiredFields?.forEach((field) => {
        let value = formValues.filter(f => f.formFieldName === field)[0]?.formFieldValue
        if(!value) {
          isValid = false
          setValidations((prev) => [...prev, field])
        }
      })

      return isValid
    }

    const savePdf = async () => {
      setValidations([])
      if (!pdfData) {
        return
      }

      if(!isFormValid()) {
        return
      }

      const pdfDoc = await PDFDocument.load(pdfData)
      if (pdfDoc) {
        const form = pdfDoc.getForm()

        // Get all the form fields (e.g., text fields)
        const fields = form.getFields()

        fields.forEach((field) => {
          const name = field.getName()
          let value = formValues.filter(f => f.formFieldName === name)[0]?.formFieldValue
          const fieldType = getFieldType(field)
          const widgets = field.acroField.getWidgets() // Get widgets for the field

          if(!value) {
            return
          }

          if(widgets && widgets.length > 0) {
            if (fieldType === 'PDFTextField') {
              // Text field
              field.setText(value)
            } else if (fieldType === 'PDFCheckBox') {
              if(value === "true") {
                field.check()
              } else {
                field.uncheck()
              }
            }
          }
        })

        const pdfBytes = await pdfDoc.save()
        const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' })
        const url = URL.createObjectURL(pdfBlob)

        setFile(url)
        handleSaveForm(formValues)
    }
  }
  

    return (
        <div>
            {file && pages?.length > 0 &&
                <div className="pdf-container">
                    <div className="pdf-utils">
                        {!defaultScale && 
                          <div className="zoom-control">
                            <Button icon={<MinusOutlined />} disabled={scale < 1} onClick={() => setScale((prevScale) => prevScale - 0.1)} />
                            <label>{`${Math.ceil(scale * 100)}%`}</label>
                            <Button icon={<PlusOutlined />} disabled={scale > 1.4} onClick={() => setScale((prevScale) => prevScale + 0.1)} />
                          </div>
                        }
                        
                        {handleDownloadForm &&
                          <button onClick={() => downloadPdf()}>
                            <span>Download</span>
                            <DownloadOutlined />
                          </button>
                        }
                      
                        {handleSaveForm &&
                          <button onClick={() => savePdf()}>
                            <span>Save</span> 
                            <SaveOutlined />
                          </button>

                        }
                        
                    </div>
                    <div className="pdf-main">
                        <div className="pdf-validations">
                          {validations &&
                            validations.map(v => {
                              return (
                                <button className="validation-button" type="button" onClick={() => handleFocus(v)}>
                                  <Tag className="validation-tag" color="error">{v} is required.</Tag>
                                </button>
                              )
                            })
                          }
                            {/* <Tag className="validation-tag" color="error">SSN Required</Tag>
                            <Tag className="validation-tag" color="error">Sample</Tag>
                            <Tag className="validation-tag" color="warning">Sample</Tag>
                            <Tag className="validation-tag" color="warning">Sample</Tag>
                            <Tag className="validation-tag" color="error">Sample</Tag> */}
                        </div>
                        <div className="pdf">
                          <Document file={file} key={file} onLoadSuccess={onLoadSuccess}>
                            {Array.from({ length: numPages }, (_, index) => {
                              const pageWidthScaled = pageSizes[index]?.width * scale
                              const pageHeightScaled = pageSizes[index]?.height * scale

                              return (
                                <div
                                  key={index}
                                  style={{
                                    position: 'relative',
                                    width: pageWidthScaled,
                                    height: pageHeightScaled,
                                  }}
                                >
                                  <Page pageNumber={index + 1} width={pageWidthScaled} />

                                  {formFields
                                    .filter(f => f.pageIndex === index)
                                    .map(f => renderFieldOverlay(f, pageHeightScaled))}
                                </div>
                              )
                            })}
                          </Document>
                        </div>

                    </div>
                </div>
            }
        </div>
    )

}

export default FormRenderer